import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import { getComic, getCategoryName, comics } from '@/mocks/comics';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const comic = id ? getComic(id) : undefined;

  const related = useMemo(
    () =>
      comic
        ? comics.filter((c) => c.categoryKey === comic.categoryKey && c.id !== comic.id).slice(0, 4)
        : [],
    [comic]
  );

  if (!comic) {
    return (
      <>
        <Seo title="漫画不存在 - 韩国漫画网 | 韩漫星球" description="您访问的漫画不存在或已被移除，返回韩漫星球继续畅读。" />
        <Navbar />
        <main className="flex min-h-[60vh] flex-col items-center justify-center px-4 pt-24 text-center">
          <h1 className="font-heading text-2xl text-foreground-900">没有找到这部漫画</h1>
          <p className="mt-3 text-sm text-foreground-500">作品可能已被移除或链接有误。</p>
          <Link to="/category" className="mt-6 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap">
            返回漫画列表
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const chapterList = Array.from({ length: Math.min(comic.chapters, 12) }, (_, i) => comic.chapters - i);

  return (
    <>
      <Seo
        title={`${comic.title} - ${comic.category}韩漫在线观看 | 韩国漫画网 | 韩漫星球`}
        description={`《${comic.title}》是${comic.author}创作的热门${comic.category}韩国漫画，评分${comic.rating}，共${comic.chapters}话，高清在线畅读。${comic.description.slice(0, 40)}…`}
        keywords={`${comic.title},${comic.category}漫画,韩国漫画网,漫画在线观看`}
        canonicalPath={`/comic/${comic.id}`}
        type="article"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: typeof window !== 'undefined' ? window.location.origin : '/' },
              { '@type': 'ListItem', position: 2, name: getCategoryName(comic.categoryKey), item: typeof window !== 'undefined' ? `${window.location.origin}/category?cat=${comic.categoryKey}` : `/category?cat=${comic.categoryKey}` },
              { '@type': 'ListItem', position: 3, name: comic.title, item: typeof window !== 'undefined' ? window.location.href : `/comic/${comic.id}` },
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'Book',
            name: comic.title,
            author: { '@type': 'Person', name: comic.author },
            genre: comic.category,
            inLanguage: 'zh-CN',
            aggregateRating: { '@type': 'AggregateRating', ratingValue: comic.rating, bestRating: 10, ratingCount: comic.likes },
            description: comic.description,
          },
        ]}
      />

      <Navbar />

      <main className="pt-20 md:pt-24">
        <section className="relative overflow-hidden border-b border-background-200">
          <img src={comic.cover} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover blur-md opacity-20" />
          <div className="absolute inset-0 bg-background-50/85"></div>
          <div className="relative mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-14">
            <nav aria-label="面包屑" className="text-sm text-foreground-500">
              <ol className="flex flex-wrap items-center gap-1">
                <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li><Link to={`/category?cat=${comic.categoryKey}`} className="hover:text-primary-600">{getCategoryName(comic.categoryKey)}</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-foreground-800">{comic.title}</li>
              </ol>
            </nav>

            <div className="mt-6 flex flex-col gap-8 md:flex-row">
              <div className="mx-auto w-44 shrink-0 md:mx-0 md:w-56">
                <img
                  src={comic.cover}
                  alt={`${comic.title} 高清封面 - ${comic.category}韩漫`}
                  className="aspect-[3/4] w-full rounded-2xl object-cover object-top border border-background-200"
                />
              </div>
              <div className="flex-1">
                <h1 className="font-heading text-2xl md:text-4xl text-foreground-900">{comic.title}</h1>
                <p className="mt-2 text-sm text-foreground-500">作者：{comic.author} · {comic.category}</p>

                <div className="mt-5 flex flex-wrap gap-4">
                  <div className="rounded-2xl bg-background-100 px-4 py-3 text-center">
                    <p className="inline-flex items-center gap-1 font-heading text-2xl text-secondary-700">
                      <i className="ri-star-fill text-secondary-500 text-lg"></i> {comic.rating.toFixed(1)}
                    </p>
                    <p className="mt-1 text-xs text-foreground-500">评分</p>
                  </div>
                  <div className="rounded-2xl bg-background-100 px-4 py-3 text-center">
                    <p className="font-heading text-2xl text-primary-600">{comic.chapters}</p>
                    <p className="mt-1 text-xs text-foreground-500">话数</p>
                  </div>
                  <div className="rounded-2xl bg-background-100 px-4 py-3 text-center">
                    <p className="font-heading text-2xl text-accent-600">{comic.views}</p>
                    <p className="mt-1 text-xs text-foreground-500">人气</p>
                  </div>
                  <div className="rounded-2xl bg-background-100 px-4 py-3 text-center">
                    <p className="inline-flex items-center gap-1 font-heading text-2xl text-primary-600">
                      <i className="ri-heart-3-fill text-lg"></i> {(comic.likes / 10000).toFixed(1)}万
                    </p>
                    <p className="mt-1 text-xs text-foreground-500">收藏</p>
                  </div>
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  {comic.tags.map((t) => (
                    <span key={t} className="rounded-full bg-accent-100 px-3 py-1 text-xs text-accent-800">{t}</span>
                  ))}
                  <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">{comic.status}</span>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <a
                    href="#chapters"
                    className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-7 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-play-circle-line"></i> 开始阅读
                  </a>
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-full border border-background-300 px-6 py-3 text-sm font-semibold text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-heart-3-line"></i> 收藏
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-full border border-background-300 px-6 py-3 text-sm font-semibold text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-share-forward-line"></i> 分享
                  </button>
                </div>

                <div className="mt-6 max-w-2xl">
                  <h2 className="text-base font-semibold text-foreground-900">作品简介</h2>
                  <p className="mt-2 text-sm leading-7 text-foreground-600">{comic.description}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="chapters" className="bg-background-50 py-10 md:py-14">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900">章节列表</h2>
                <p className="mt-1 text-sm text-foreground-500">共 {comic.chapters} 话，这里展示最近 {chapterList.length} 话。</p>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {chapterList.map((n) => (
                <a
                  key={n}
                  href="#chapters"
                  className="group flex items-center justify-between rounded-xl border border-background-200 bg-background-100 px-4 py-3 transition-colors hover:border-primary-300 hover:bg-background-50"
                >
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600">第 {n} 话</span>
                  <span className="inline-flex items-center gap-1 text-xs text-foreground-400">
                    <i className="ri-eye-line"></i> {Math.round((comic.likes / n) * 100)} 阅读
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {related.length > 0 && (
          <section className="bg-background-100 py-10 md:py-14">
            <div className="mx-auto max-w-7xl px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900">相关推荐</h2>
              <p className="mt-1 text-sm text-foreground-500">更多好看的{comic.category}韩国漫画等你发现。</p>
              <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {related.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}